// La operacion que el Brain se detuvo a preguntar.
//
// **Por que una tarjeta dentro de la conversacion y no un `alert-dialog`**, que
// existe y seria lo obvio: un dialogo tapa la conversacion y desaparece al
// cerrarse, y aqui lo que se decide forma parte del historial —«esto te lo
// pregunte, y dijiste que no»—. Con un dialogo, esa decision no deja rastro en
// ningun sitio que el usuario vuelva a mirar.
//
// La guarda que de verdad impide la ejecucion vive en el servidor
// (`rag-api/agents/tools/tool_factory.py`). Esta tarjeta **no protege nada**:
// solo pregunta. Si alguien la quitase, el Brain seguiria sin ejecutar.
//
// **El orden no es estetico** (D3, 4.2): delante que es —la operacion y sobre
// que—, detras que pasara si dices que si. Hasta el 2026-09-22 la consecuencia
// iba pegada al titular —«Voy a duplicar un agente. Esto no se puede
// deshacer.»— y es lo mismo que se aprendio en Knowledge el 2026-09-21: con la
// consecuencia de titular, cuatro propuestas distintas se leen iguales, porque
// lo que las distingue queda detras de una frase que es siempre la misma.

import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalloutCard } from '@/components/ui/callout-card'
import type { BrainToolCall } from '@/services/brainApi'

interface Props {
  accion: NonNullable<BrainToolCall['accion']>
  onConfirmar: () => void
  onCancelar: () => void
  ocupado?: boolean
}

/** Los argumentos en palabras, para que la pregunta diga **sobre que**. */
function sobreQue(argumentos?: Record<string, unknown>): string | null {
  const entradas = Object.entries(argumentos ?? {}).filter(
    ([, valor]) => valor !== null && valor !== undefined && valor !== '',
  )
  if (!entradas.length) return null
  return entradas.map(([clave, valor]) => `${clave}: ${String(valor)}`).join(' · ')
}

export function ConfirmacionPendiente({ accion, onConfirmar, onCancelar, ocupado }: Props) {
  const { t } = useTranslation()
  const detalle = sobreQue(accion.argumentos)

  // La envoltura vive en `CalloutCard` desde el 2026-09-23: la escribian a
  // mano esta y `FileProposals`, y el presupuesto de cajas lo cazo. Antes de
  // eso decia `bg-warning/60 p-3/20`: el `/20` se habia colado en la clase de
  // al lado, asi que la tarjeta se quedaba sin relleno y el fondo salia a 60%.
  return (
    <CalloutCard icon={AlertTriangle} tone="warning" className="mt-3">
      <p className="text-sm font-medium">
        {t('brain.confirmar_titulo', { accion: accion.nombre_visible.toLowerCase() })}
      </p>
      {detalle && (
        <p className="mt-1 break-words font-mono text-xs text-muted-foreground">{detalle}</p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">{t('brain.confirmar_consecuencia')}</p>
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={onConfirmar} disabled={ocupado}>
          {ocupado ? t('brain.ejecutando') : t('brain.confirmar')}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancelar} disabled={ocupado}>
          {t('brain.cancelar')}
        </Button>
      </div>
    </CalloutCard>
  )
}
